import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { editor as MonacoEditor } from 'monaco-editor';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { projectSlice } from '../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { LAYOUT_CONSTANTS } from '../../utils/layout-constants';
import LeftDrawer, { LeftDrawerTab } from '../Drawers/LeftDrawer';
import HelpPopover from '../HelpPopover';
// Build panel sub-tabs (Mechanisms, Motors, Sensors, Other)
import BuildPanelTab from './ConfigurationTabs/BuildPanelTab';
import CommandManagementControl from './ConfigurationTabs/CommandManagementControl';
import ConfigurePanelTab from './ConfigurationTabs/ConfigurePanelTab';
import ControlPanelTab from './ConfigurationTabs/ControlPanelTab';
import { ActionsIcon, ElectronicsIcon, InputsIcon, ControlModeIcon } from '../Icons/Editor Panels';
import type { ICardComponent } from './Devices/DeviceCardBuilder';
import type { CodeSnippetCategoryHeader } from './Shared/CodeContent';
import type {
  CodeSnippetInsertionOutcome,
  CodeSnippetInsertionPayload,
  CodeSnippetPlacementFilter,
  CodeSnippetSourceFilter,
  EditorState,
} from './types';
import {
  API_SNIPPET_BROWSER_ID,
  API_SNIPPET_NAVIGATION_INSTRUCTIONS_ID,
  API_SNIPPET_NAVIGATION_ITEM_SELECTOR,
  API_SNIPPET_PANE_ID,
} from './Snippets/snippetKeyboardNavigation';

const ApiContent = React.lazy(() => import('./Snippets/ApiContent'));

const isTextEntryTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="combobox"]'));

interface EditorDrawerProps {
  drawerWidth: number;
  isOpen: boolean;
  editor: MonacoEditor.IStandaloneCodeEditor | null;
  editorState?: EditorState;
  hardwareDynamicCards: ICardComponent[];
  hardwareIsLoading: boolean;
  driveUnitPortRegistryRev: number;
  onInsertSnippet?: (payload: CodeSnippetInsertionPayload) => CodeSnippetInsertionOutcome;
}

interface TabConfig {
  id: string;
  label: string;
  icon: JSX.Element;
  value: string;
  type: 'config' | 'snippet';
  content: React.ReactNode;
  anchorBottom?: boolean;
  stickyHeader?: boolean;
  headerContent?: React.ReactNode;
}

const EditorDrawer: React.FC<EditorDrawerProps> = ({
  drawerWidth,
  isOpen,
  editor,
  hardwareDynamicCards,
  hardwareIsLoading,
  driveUnitPortRegistryRev,
  onInsertSnippet,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [apiSnippetFilter, setApiSnippetFilter] = useState('');
  const [apiSnippetPlacementFilter, setApiSnippetPlacementFilter] = useState<CodeSnippetPlacementFilter>('all');
  const [apiSnippetSourceFilter, setApiSnippetSourceFilter] = useState<CodeSnippetSourceFilter>('all');
  const [apiSnippetHideDisabled, setApiSnippetHideDisabled] = useState(false);
  const [apiSnippetSelectedCategoryKey, setApiSnippetSelectedCategoryKey] = useState<string | null>(null);
  const [apiSnippetFiltersExpanded, setApiSnippetFiltersExpanded] = useState(false);
  const [apiSnippetSelectedCategoryHeader, setApiSnippetSelectedCategoryHeader] = useState<CodeSnippetCategoryHeader | null>(null);
  const [apiSnippetResultCount, setApiSnippetResultCount] = useState<number | null>(null);
  const apiSnippetFilterInputRef = useRef<HTMLInputElement | null>(null);

  const selectedTab = useAppSelector((state) => state.project.loadedProject?.selectedEditorDrawerTab);
  const isApiSnippetSearchActive = apiSnippetFilter.trim().length > 0;

  const handleBackToApiSnippetCategories = useCallback(() => {
    setApiSnippetSelectedCategoryKey(null);
    setApiSnippetSelectedCategoryHeader(null);
  }, []);

  const handleApiSnippetSelectedCategoryHeaderChange = useCallback((nextHeader: CodeSnippetCategoryHeader | null) => {
    setApiSnippetSelectedCategoryHeader((prevHeader) => {
      if (
        prevHeader?.title === nextHeader?.title &&
        prevHeader?.description === nextHeader?.description &&
        prevHeader?.sectionTitle === nextHeader?.sectionTitle
      ) {
        return prevHeader;
      }

      return nextHeader;
    });
  }, []);

  const handleVisibleApiSnippetCountChange = useCallback((count: number) => {
    setApiSnippetResultCount(count);
  }, []);

  const activeApiSnippetFilterCount = useMemo(
    () => [apiSnippetSourceFilter !== 'all', apiSnippetPlacementFilter !== 'all', apiSnippetHideDisabled].filter(Boolean).length,
    [apiSnippetHideDisabled, apiSnippetPlacementFilter, apiSnippetSourceFilter]
  );
  const apiSnippetFiltersAreActive = isApiSnippetSearchActive || activeApiSnippetFilterCount > 0;
  const apiSnippetResultStatus =
    !apiSnippetFiltersAreActive || apiSnippetResultCount == null
      ? ''
      : apiSnippetResultCount === 1
      ? t('editorDrawer.apiFilter.resultCount.one', '{{count}} API snippet available.', { count: apiSnippetResultCount })
      : t('editorDrawer.apiFilter.resultCount.other', '{{count}} API snippets available.', { count: apiSnippetResultCount });
  const apiSnippetResultStatusKey = `${apiSnippetFilter}|${apiSnippetSourceFilter}|${apiSnippetPlacementFilter}|${apiSnippetHideDisabled}|${apiSnippetResultCount}`;

  const apiSnippetFilterToggleAriaLabel = apiSnippetFiltersExpanded
    ? activeApiSnippetFilterCount > 0
      ? t('editorDrawer.apiFilter.toggle.hideActiveAriaLabel', 'Hide API filters ({{count}} active)', { count: activeApiSnippetFilterCount })
      : t('editorDrawer.apiFilter.toggle.hideAriaLabel', 'Hide API filters')
    : activeApiSnippetFilterCount > 0
    ? t('editorDrawer.apiFilter.toggle.showActiveAriaLabel', 'Show API filters ({{count}} active)', { count: activeApiSnippetFilterCount })
    : t('editorDrawer.apiFilter.toggle.showAriaLabel', 'Show API filters');

  const apiSnippetFilterControl = useMemo(() => {
    const sourceFilterOptions: Array<{ value: CodeSnippetSourceFilter; label: string }> = [
      { value: 'all', label: t('editorDrawer.apiFilter.source.all', 'All sources') },
      { value: 'sphero', label: t('editorDrawer.apiFilter.source.sphero', 'Sphero') },
      { value: 'micropython', label: t('editorDrawer.apiFilter.source.microPython', 'MicroPython') },
      { value: 'configured', label: t('editorDrawer.apiFilter.source.myRobot', 'My robot') },
    ];
    const placementFilterOptions: Array<{ value: CodeSnippetPlacementFilter; label: string }> = [
      { value: 'all', label: t('editorDrawer.apiFilter.codeType.all', 'All') },
      { value: 'top-level-setup', label: t('editorDrawer.apiFilter.codeType.regularCode', 'Immediate code') },
      { value: 'action-group', label: t('editorDrawer.apiFilter.codeType.robotBaseActions', 'RobotBase actions') },
    ];
    const sourceFilterLabel = t('editorDrawer.apiFilter.source.label', 'Source');
    const sourceFilterHelp = t(
      'editorDrawer.apiFilter.source.help',
      'My robot shows snippets for hardware and actions already set up in this program.'
    );
    const sourceFilterHelpAriaLabel = t('editorDrawer.apiFilter.source.helpAriaLabel', 'Learn about Source filter');
    const placementFilterLabel = t('editorDrawer.apiFilter.codeType.label', 'Code type');
    const placementFilterHelp = t(
      'editorDrawer.apiFilter.codeType.help',
      'Immediate code runs when the program reaches it. RobotBase actions run later when RobotBase starts them in routines or mappings.'
    );
    const placementFilterHelpAriaLabel = t('editorDrawer.apiFilter.codeType.helpAriaLabel', 'Learn about Code type filter');
    const apiSnippetFiltersLabel =
      activeApiSnippetFilterCount > 0
        ? t('editorDrawer.apiFilter.filtersActiveLabel', 'Filters ({{count}} active)', { count: activeApiSnippetFilterCount })
        : t('editorDrawer.apiFilter.filtersLabel', 'Filters');
    const filterSelectControlSx = {
      '& .MuiInputLabel-root': {
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 700,
      },
      '& .MuiSelect-select': {
        color: 'text.primary',
        fontSize: 14,
        fontWeight: 600,
        py: 0.875,
      },
      '& .MuiSelect-icon': {
        color: 'text.secondary',
      },
      '& .MuiInput-underline:before': {
        borderBottomColor: 'divider',
      },
      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
        borderBottomColor: 'text.secondary',
      },
    };

    return (
      <Stack spacing={1} sx={{ width: '100%' }}>
        <CommandManagementControl />
        <Stack
          spacing={apiSnippetFiltersExpanded ? 0.75 : 0}
          sx={{
            bgcolor: apiSnippetFiltersExpanded ? 'action.hover' : 'transparent',
            borderRadius: apiSnippetFiltersExpanded ? 1 : 0,
            mb: apiSnippetFiltersExpanded ? 0.5 : 0,
            px: apiSnippetFiltersExpanded ? 0.75 : 0,
            py: apiSnippetFiltersExpanded ? 0.75 : 0,
            width: '100%',
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%' }}>
            <TextField
              id="api-snippet-filter-input"
              inputRef={apiSnippetFilterInputRef}
              value={apiSnippetFilter}
              onChange={(event) => setApiSnippetFilter(event.target.value)}
              placeholder={t('editorDrawer.apiFilter.placeholder', 'Filter snippets (/)')}
              size="small"
              fullWidth
              slotProps={{
                htmlInput: {
                  'aria-label': t('editorDrawer.apiFilter.ariaLabel', 'Filter API snippets'),
                  'aria-keyshortcuts': '/',
                  'aria-describedby': API_SNIPPET_NAVIGATION_INSTRUCTIONS_ID,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: apiSnippetFilter ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('editorDrawer.apiFilter.clear', 'Clear API snippet filter')}
                        edge="end"
                        size="small"
                        onClick={() => setApiSnippetFilter('')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              sx={{
                minWidth: 0,
                '& .MuiInputBase-root': {
                  fontSize: 13,
                  backgroundColor: 'background.default',
                },
              }}
            />
            <IconButton
              size="small"
              aria-expanded={apiSnippetFiltersExpanded}
              aria-controls="api-snippet-filter-controls"
              aria-label={apiSnippetFilterToggleAriaLabel}
              onClick={() => setApiSnippetFiltersExpanded((prev) => !prev)}
              sx={{
                bgcolor: apiSnippetFiltersExpanded ? 'background.default' : 'transparent',
                color: apiSnippetFiltersExpanded || activeApiSnippetFilterCount > 0 ? 'primary.main' : 'text.primary',
                flexShrink: 0,
                height: 40,
                overflow: 'visible',
                position: 'relative',
                width: 40,
                '&:hover': {
                  bgcolor: apiSnippetFiltersExpanded ? 'background.default' : 'action.hover',
                },
              }}
            >
              <FilterAltIcon fontSize="small" />
              {activeApiSnippetFilterCount > 0 ? (
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    bgcolor: 'primary.main',
                    border: 2,
                    borderColor: 'background.paper',
                    borderRadius: '50%',
                    color: 'primary.contrastText',
                    fontSize: 10,
                    fontWeight: 700,
                    height: 18,
                    lineHeight: '14px',
                    minWidth: 18,
                    pointerEvents: 'none',
                    position: 'absolute',
                    right: -4,
                    textAlign: 'center',
                    top: -4,
                  }}
                >
                  {activeApiSnippetFilterCount}
                </Box>
              ) : null}
            </IconButton>
          </Stack>
          <Collapse in={apiSnippetFiltersExpanded} timeout="auto" unmountOnExit>
            <Stack
              id="api-snippet-filter-controls"
              spacing={1}
              sx={{
                width: '100%',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                  {apiSnippetFiltersLabel}
                </Typography>
                {activeApiSnippetFilterCount > 0 ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setApiSnippetSourceFilter('all');
                      setApiSnippetPlacementFilter('all');
                      setApiSnippetHideDisabled(false);
                    }}
                    sx={{ color: 'text.secondary', minWidth: 0, px: 0.75, textTransform: 'none' }}
                  >
                    {t('editorDrawer.apiFilter.clearAll', 'Clear')}
                  </Button>
                ) : null}
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <FormControl size="small" fullWidth variant="standard" sx={{ ...filterSelectControlSx, minWidth: 0 }}>
                  <InputLabel id="api-snippet-source-filter-label">{sourceFilterLabel}</InputLabel>
                  <Select
                    labelId="api-snippet-source-filter-label"
                    id="api-snippet-source-filter"
                    value={apiSnippetSourceFilter}
                    label={sourceFilterLabel}
                    onChange={(event: SelectChangeEvent) => setApiSnippetSourceFilter(event.target.value as CodeSnippetSourceFilter)}
                  >
                    {sourceFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  <HelpPopover id="api-snippet-source-filter-help" text={sourceFilterHelp} ariaLabel={sourceFilterHelpAriaLabel} />
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <FormControl size="small" fullWidth variant="standard" sx={{ ...filterSelectControlSx, minWidth: 0 }}>
                  <InputLabel id="api-snippet-placement-filter-label">{placementFilterLabel}</InputLabel>
                  <Select
                    labelId="api-snippet-placement-filter-label"
                    id="api-snippet-placement-filter"
                    value={apiSnippetPlacementFilter}
                    label={placementFilterLabel}
                    onChange={(event: SelectChangeEvent) => setApiSnippetPlacementFilter(event.target.value as CodeSnippetPlacementFilter)}
                  >
                    {placementFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  <HelpPopover id="api-snippet-code-type-filter-help" text={placementFilterHelp} ariaLabel={placementFilterHelpAriaLabel} />
                </Box>
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={apiSnippetHideDisabled}
                    onChange={(event) => setApiSnippetHideDisabled(event.target.checked)}
                    slotProps={{
                      input: {
                        'aria-label': t('editorDrawer.apiFilter.hideDisabled.ariaLabel', 'Hide disabled API snippets'),
                      },
                    }}
                  />
                }
                label={t('editorDrawer.apiFilter.hideDisabled.label', 'Hide disabled snippets')}
                sx={{
                  m: 0,
                  '& .MuiCheckbox-root': {
                    ml: -0.25,
                    p: 0.5,
                  },
                  '& .MuiFormControlLabel-label': {
                    color: 'text.primary',
                    fontSize: 14,
                    fontWeight: 600,
                  },
                }}
              />
            </Stack>
          </Collapse>
        </Stack>
        {apiSnippetSelectedCategoryKey && !isApiSnippetSearchActive ? (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: '100%', pt: 0.25, pb: 0.5 }}>
            <IconButton
              size="small"
              onClick={handleBackToApiSnippetCategories}
              aria-label={t('snippets.category.back', 'Back to categories')}
              sx={{
                flexShrink: 0,
                height: 28,
                width: 28,
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            {apiSnippetSelectedCategoryHeader ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {apiSnippetSelectedCategoryHeader.sectionTitle ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2 }}>
                    {apiSnippetSelectedCategoryHeader.sectionTitle}
                  </Typography>
                ) : null}
                <Typography variant="h6" component="div" sx={{ lineHeight: 1.15, overflowWrap: 'anywhere' }}>
                  {apiSnippetSelectedCategoryHeader.title}
                </Typography>
                {apiSnippetSelectedCategoryHeader.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
                    {apiSnippetSelectedCategoryHeader.description}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Stack>
        ) : null}
        <Box
          key={apiSnippetResultStatusKey}
          data-testid="api-snippet-result-status"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            whiteSpace: 'nowrap',
            width: 1,
          }}
        >
          {apiSnippetResultStatus}
        </Box>
      </Stack>
    );
  }, [
    activeApiSnippetFilterCount,
    apiSnippetFilter,
    apiSnippetFilterToggleAriaLabel,
    apiSnippetFiltersExpanded,
    apiSnippetHideDisabled,
    apiSnippetPlacementFilter,
    apiSnippetResultStatus,
    apiSnippetResultStatusKey,
    apiSnippetSelectedCategoryHeader,
    apiSnippetSelectedCategoryKey,
    apiSnippetSourceFilter,
    handleBackToApiSnippetCategories,
    isApiSnippetSearchActive,
    t,
  ]);

  const allTabs: TabConfig[] = useMemo(
    () => [
      {
        id: 'config-mechanisms',
        label: t('editorDrawer.tabs.build', 'Build'),
        icon: <ElectronicsIcon />,
        value: 'config-mechanisms',
        type: 'config' as const,
        content: <BuildPanelTab />,
      },
      {
        id: 'config-configure',
        label: t('editorDrawer.tabs.configure', 'Configure'),
        icon: <ControlModeIcon />,
        value: 'config-configure',
        type: 'config' as const,
        content: (
          <ConfigurePanelTab
            editor={editor}
            hardwareDynamicCards={hardwareDynamicCards}
            hardwareIsLoading={hardwareIsLoading}
            driveUnitPortRegistryRev={driveUnitPortRegistryRev}
          />
        ),
      },
      {
        id: 'config-inputs',
        label: t('editorDrawer.tabs.control', 'Control'),
        icon: <InputsIcon />,
        value: 'config-inputs',
        type: 'config' as const,
        content: <ControlPanelTab editor={editor ?? undefined} />,
      },
      {
        id: 'snippet-api',
        label: t('editorDrawer.tabs.api', 'API'),
        icon: <ActionsIcon />,
        value: 'snippet-api',
        type: 'snippet' as const,
        content: (
          <React.Suspense fallback={null}>
            <ApiContent
              filterText={apiSnippetFilter}
              placementFilter={apiSnippetPlacementFilter}
              sourceFilter={apiSnippetSourceFilter}
              hideDisabledSnippets={apiSnippetHideDisabled}
              selectedCategoryKey={apiSnippetSelectedCategoryKey}
              onSelectCategory={setApiSnippetSelectedCategoryKey}
              onBackToCategories={handleBackToApiSnippetCategories}
              hideBackControl
              hideSelectedCategoryHeader
              onSelectedCategoryHeaderChange={handleApiSnippetSelectedCategoryHeaderChange}
              onInsertSnippet={onInsertSnippet}
              onVisibleSnippetCountChange={handleVisibleApiSnippetCountChange}
            />
          </React.Suspense>
        ),
        anchorBottom: true,
        stickyHeader: true,
        headerContent: apiSnippetFilterControl,
      },
    ],
    [
      t,
      editor,
      hardwareDynamicCards,
      hardwareIsLoading,
      driveUnitPortRegistryRev,
      apiSnippetFilter,
      apiSnippetHideDisabled,
      apiSnippetPlacementFilter,
      apiSnippetSourceFilter,
      apiSnippetFilterControl,
      apiSnippetSelectedCategoryKey,
      handleApiSnippetSelectedCategoryHeaderChange,
      handleVisibleApiSnippetCountChange,
      handleBackToApiSnippetCategories,
      onInsertSnippet,
    ]
  );

  const visibleTabs = allTabs;

  const configTabs = useMemo(() => visibleTabs.filter((tab) => tab.type === 'config'), [visibleTabs]);
  const snippetTabs = useMemo(() => visibleTabs.filter((tab) => tab.type === 'snippet'), [visibleTabs]);

  // Ensure the selected tab is one of the available tabs.
  const effectiveSelectedTab = useMemo(() => {
    const isVisible = visibleTabs.some((tab) => tab.value === selectedTab);
    if (isVisible) return selectedTab;
    // Stored tab is not available anymore; fall back to the first tab only if a tab was selected.
    if (selectedTab == null) return null;
    const firstVisible = visibleTabs[0];
    return firstVisible ? firstVisible.value : null;
  }, [visibleTabs, selectedTab]);

  // Sync Redux when stored tab was invalid so we don't pass an invalid value on next load
  useEffect(() => {
    if (selectedTab != null && !visibleTabs.some((tab) => tab.value === selectedTab) && effectiveSelectedTab != null) {
      dispatch(projectSlice.actions.setSelectedEditorDrawerTab(effectiveSelectedTab));
    }
  }, [visibleTabs, selectedTab, effectiveSelectedTab, dispatch]);

  const isContentOpen = isOpen && effectiveSelectedTab !== null;
  const isApiSnippetPaneOpen = isContentOpen && effectiveSelectedTab === 'snippet-api';

  const focusApiSnippetPane = useCallback(() => {
    const browser = document.getElementById(API_SNIPPET_BROWSER_ID);
    const rememberedControl = browser?.querySelector<HTMLElement>(`${API_SNIPPET_NAVIGATION_ITEM_SELECTOR}[tabindex="0"]`);
    (rememberedControl ?? apiSnippetFilterInputRef.current)?.focus();
  }, []);

  useEffect(() => {
    if (!isApiSnippetPaneOpen || !editor) return;

    const handlePaneNavigation = (event: KeyboardEvent) => {
      const pane = document.getElementById(API_SNIPPET_PANE_ID);
      const activeElement = document.activeElement;
      const activeInApiPane = Boolean(pane && activeElement && pane.contains(activeElement));

      if (event.key === 'F6' && !event.altKey && !event.ctrlKey && !event.metaKey) {
        const editorDomNode = editor.getDomNode();
        const activeInEditor = Boolean((editorDomNode && activeElement && editorDomNode.contains(activeElement)) || editor.hasTextFocus());
        if (!activeInApiPane && !activeInEditor) return;

        event.preventDefault();
        event.stopPropagation();
        if (activeInApiPane) {
          editor.focus();
        } else {
          focusApiSnippetPane();
        }
        return;
      }

      if (event.key === '/' && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && activeInApiPane) {
        if (isTextEntryTarget(activeElement)) return;

        event.preventDefault();
        event.stopPropagation();
        apiSnippetFilterInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handlePaneNavigation, true);
    return () => window.removeEventListener('keydown', handlePaneNavigation, true);
  }, [editor, focusApiSnippetPane, isApiSnippetPaneOpen]);

  const currentTabConfig = useMemo(() => {
    return visibleTabs.find((tab) => tab.value === effectiveSelectedTab);
  }, [visibleTabs, effectiveSelectedTab]);

  const handleTabClick = useCallback(
    (tabValue: string) => {
      if (effectiveSelectedTab === tabValue) {
        // Clicking the same tab toggles the drawer closed
        dispatch(projectSlice.actions.setSelectedEditorDrawerTab(null));
      } else {
        dispatch(projectSlice.actions.setSelectedEditorDrawerTab(tabValue));
      }
      // Open the drawer if it's not already open
      if (!isOpen) {
        setTimeout(() => dispatch(projectSlice.actions.toggleEditorOpenDrawer()), 50);
      }
    },
    [effectiveSelectedTab, isOpen, dispatch]
  );

  const handleCloseDrawer = useCallback(() => {
    dispatch(projectSlice.actions.setSelectedEditorDrawerTab(null));
  }, [dispatch]);

  const sharedTabs: LeftDrawerTab[] = [...configTabs, ...snippetTabs].map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    value: tab.value,
    anchorBottom: tab.anchorBottom,
    stickyHeader: tab.stickyHeader,
    headerContent: tab.headerContent,
  }));

  const handleWidthChange = useCallback(
    (width: number) => {
      dispatch(projectSlice.actions.setLeftDrawerContentWidth({ context: 'codeEditor', width }));
    },
    [dispatch]
  );

  return (
    <LeftDrawer
      drawerWidth={drawerWidth}
      isOpen={isContentOpen}
      tabs={sharedTabs}
      selectedTab={effectiveSelectedTab ?? null}
      onTabClick={handleTabClick}
      onClose={handleCloseDrawer}
      onWidthChange={handleWidthChange}
      contentScrollResetKey={effectiveSelectedTab === 'snippet-api' && apiSnippetSelectedCategoryKey ? `api:${apiSnippetSelectedCategoryKey}` : null}
      contentRegionId={isApiSnippetPaneOpen ? API_SNIPPET_PANE_ID : undefined}
      contentRegionLabel={isApiSnippetPaneOpen ? t('editorDrawer.apiPane.ariaLabel', 'API snippets panel') : undefined}
      contentRegionDescriptionId={isApiSnippetPaneOpen ? API_SNIPPET_NAVIGATION_INSTRUCTIONS_ID : undefined}
      contentRegionKeyShortcuts={isApiSnippetPaneOpen ? 'F6 Shift+F6' : undefined}
      minWidth={LAYOUT_CONSTANTS.LEFT_DRAWER_LIMITS.CODE_EDITOR.MIN}
      maxWidth={LAYOUT_CONSTANTS.LEFT_DRAWER_LIMITS.CODE_EDITOR.MAX}
      getHeader={(value) => {
        const tab = [...configTabs, ...snippetTabs].find((t) => t.value === value) || currentTabConfig;
        return { icon: tab ? (tab.icon as JSX.Element) : null, label: tab ? tab.label : '' };
      }}
      renderContent={(value) => {
        return (value ? [...configTabs, ...snippetTabs].find((t) => t.value === value) : currentTabConfig)?.content || null;
      }}
    />
  );
};

export default EditorDrawer;
